import { formatMoney } from '@/lib/currencyFormat';
import { useState } from 'react';
import { devLog } from '@/utils/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  Receipt,
  X,
  Download,
  Printer,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  User,
  Building,
  Calculator
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { usePayslipDetails, usePayslipLineItems } from '@/hooks/payslips';
import { PayslipLineItemsTable } from './PayslipLineItemsTable';
import { PayslipSummaryCard } from './PayslipSummaryCard';
import { PayslipActionsMenu } from './PayslipActionsMenu';
import { generatePayslipPdf } from '@/services/payslips/pdfGenerator';
import { exportPayslipDetailToCSV, exportPayslipDetailToJSON } from '@/services/payslips/exportService';
import { toast } from 'sonner';

interface PayslipDetailDialogProps {
  payslipId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PayslipDetailDialog({
  payslipId,
  open,
  onOpenChange,
}: PayslipDetailDialogProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showSensitiveData, setShowSensitiveData] = useState(true);
  const [isComparisonExpanded, setIsComparisonExpanded] = useState(false);
  const { currencyCode } = useWorkplaceCurrency();

  // Fetch detailed payslip data
  const {
    data: payslipDetails,
    isLoading: detailsLoading,
    error: detailsError
  } = usePayslipDetails(payslipId || undefined);

  // Fetch line items separately for performance
  const {
    data: lineItems,
    isLoading: lineItemsLoading,
    error: lineItemsError
  } = usePayslipLineItems(payslipId || undefined);

  if (!payslipId) {
    return null;
  }

  const formatCurrency = (amount: number) => {
    return formatMoney(amount, payslipDetails?.payslip?.currency_code || currencyCode);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (detailsLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Loading Payslip Details...</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (detailsError || !payslipDetails) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Error Loading Payslip</DialogTitle>
            <DialogDescription>
              We couldn't load the payslip details. Please try again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const { payslip, previous_payslip, year_to_date_summary } = payslipDetails;
  const periodDays = differenceInDays(
    new Date(payslip.end_date),
    new Date(payslip.start_date)
  ) + 1;

  const hasLineItems = lineItems && lineItems.length > 0;
  const totalDeductions = payslip.calculated_totals?.total_deductions || 0;
  const grossEarnings = payslip.calculated_totals?.gross_earnings || Number(payslip.gross_pay);
  const netPay = payslip.calculated_totals?.net_pay || Number(payslip.net_pay || 0);

  // Calculate comparison with previous payslip
  const previousGross = previous_payslip ? Number(previous_payslip.gross_pay) : 0;
  const grossChange = previousGross ? ((grossEarnings - previousGross) / previousGross) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent key={`payslip-${payslipId}-${showSensitiveData}`} className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Receipt className="h-6 w-6 text-primary" />
              <div>
                <DialogTitle className="text-xl">
                  Payslip Details
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Pay Period: {format(new Date(payslip.start_date), 'MMM dd')} - {format(new Date(payslip.end_date), 'MMM dd, yyyy')} 
                  <span className="ml-2 text-muted-foreground">({periodDays} days)</span>
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSensitiveData(!showSensitiveData)}
              >
                {showSensitiveData ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Details
                  </>
                )}
              </Button>
              <PayslipActionsMenu
                payslip={payslip}
                onExport={async (fmt) => {
                  if (fmt === 'pdf') {
                    try {
                      toast.loading('Generating PDF...', { id: 'pdf-export' });
                      const blob = await generatePayslipPdf(payslipDetails, lineItems, { currencyCode });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `payslip_${format(new Date(payslip.start_date), 'yyyy-MM-dd')}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('PDF downloaded', { id: 'pdf-export' });
                    } catch (err) {
                      console.error('PDF export failed:', err);
                      toast.error('Failed to generate PDF', { id: 'pdf-export' });
                    }
                  } else if (fmt === 'csv') {
                    try {
                      exportPayslipDetailToCSV(payslipDetails, lineItems ?? undefined);
                      toast.success('CSV downloaded');
                    } catch (err) {
                      console.error('CSV export failed:', err);
                      toast.error('Failed to export CSV');
                    }
                  } else if (fmt === 'json') {
                    try {
                      exportPayslipDetailToJSON(payslipDetails, lineItems ?? undefined);
                      toast.success('JSON downloaded');
                    } catch (err) {
                      console.error('JSON export failed:', err);
                      toast.error('Failed to export JSON');
                    }
                  }
                }}
                onPrint={() => window.print()}
              />
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Key Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Gross Pay</p>
                    <p className="text-base font-bold text-green-600">
                      {showSensitiveData ? formatCurrency(grossEarnings) : '****'}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-600" />
                </div>
                {previous_payslip && (
                  <div className="flex items-center gap-1 text-xs mt-2">
                    <TrendingUp 
                      className={cn(
                        "h-3 w-3",
                        grossChange >= 0 ? "text-green-600" : "text-red-600"
                      )}
                    />
                    <span className={grossChange >= 0 ? "text-green-600" : "text-red-600"}>
                      {grossChange >= 0 ? '+' : ''}{grossChange.toFixed(1)}% vs last period
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Deductions</p>
                    <p className="text-base font-bold text-red-600">
                      {showSensitiveData ? formatCurrency(totalDeductions) : '****'}
                    </p>
                  </div>
                  <Calculator className="h-8 w-8 text-red-600" />
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {((totalDeductions / grossEarnings) * 100).toFixed(1)}% of gross
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Net Pay</p>
                    <p className="text-base font-bold">
                      {showSensitiveData ? formatCurrency(netPay) : '****'}
                    </p>
                  </div>
                  <Receipt className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {((netPay / grossEarnings) * 100).toFixed(1)}% of gross
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Hours</p>
                    <p className="text-base font-bold">
                      {Number(payslip.total_hours).toFixed(1)}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {(Number(payslip.total_hours) / periodDays).toFixed(1)} hrs/day avg
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Year-to-Date Summary (Collapsible) */}
          {year_to_date_summary.periods_count > 1 && (
            <Card>
              <CardHeader className="pb-3">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsComparisonExpanded(!isComparisonExpanded)}
                >
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Year-to-Date Summary
                  </CardTitle>
                  {isComparisonExpanded ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </CardHeader>
              {isComparisonExpanded && (
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">YTD Gross Earnings</p>
                      <p className="text-xl font-bold text-green-600">
                        {showSensitiveData ? formatCurrency(year_to_date_summary.total_gross) : '****'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {year_to_date_summary.periods_count} pay periods
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">YTD Net Pay</p>
                      <p className="text-xl font-bold">
                        {showSensitiveData ? formatCurrency(year_to_date_summary.total_net) : '****'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatPercentage(year_to_date_summary.total_net / year_to_date_summary.total_gross)} of gross
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">YTD Hours</p>
                      <p className="text-xl font-bold">
                        {year_to_date_summary.total_hours.toFixed(1)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(year_to_date_summary.total_hours / year_to_date_summary.periods_count).toFixed(1)} hrs/period avg
                      </p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Detailed Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Line Items
              </TabsTrigger>
              <TabsTrigger value="taxes" className="flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Tax Details
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <PayslipSummaryCard
                payslip={payslip}
                showSensitiveData={showSensitiveData}
                previousPayslip={previous_payslip}
              />
            </TabsContent>

            {/* Line Items Tab */}
            <TabsContent value="breakdown" className="space-y-4">
              {hasLineItems ? (
                <PayslipLineItemsTable
                  lineItems={lineItems}
                  isLoading={lineItemsLoading}
                  showSensitiveData={showSensitiveData}
                />
              ) : (
                <Card>
                  <CardContent className="p-8">
                    <div className="text-center text-muted-foreground">
                      <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No Detailed Breakdown Available</h3>
                      <p>
                        This payslip uses the legacy calculation method. 
                        Detailed line items are available for newer payslips.
                      </p>
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Gross Pay:</span>
                            <span className="float-right">
                              {showSensitiveData ? formatCurrency(grossEarnings) : '****'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Total Hours:</span>
                            <span className="float-right">
                              {Number(payslip.total_hours).toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Tax Details Tab */}
            <TabsContent value="taxes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Tax Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {payslip.calculated_totals?.tax_breakdown && 
                   Object.keys(payslip.calculated_totals.tax_breakdown).length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(payslip.calculated_totals.tax_breakdown).map(([taxType, amount]) => (
                          <div key={taxType} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                            <span className="font-medium capitalize">
                              {taxType.replace(/_/g, ' ')}
                            </span>
                            <span className="font-bold">
                              {showSensitiveData ? formatCurrency(Number(amount)) : '****'}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center text-lg font-bold">
                        <span>Total Tax Deductions:</span>
                        <span className="text-red-600">
                          {showSensitiveData ? formatCurrency(
                            Object.values(payslip.calculated_totals.tax_breakdown)
                              .reduce((sum, amount) => sum + Number(amount), 0)
                          ) : '****'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No Tax Breakdown Available</h3>
                      <p>
                        Detailed tax information is not available for this payslip.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}